// The fixed-cadence heart of the client: predict own movement every frame
// with the same stepPlayer the server runs, and send the current intent at
// the server's tick rate.

import {
  boostActive,
  type InputMessage,
  PATCH_INTERVAL_MS,
  planFor,
  shipEnvFor,
  shipFlightTuning,
  skiffPerformance,
  stepPlayer,
  stepShip,
  stepSkiff,
} from "@bebop/shared";
import type { InputController } from "./input";
import type { NetClient } from "./net";
import type { World } from "./world";

/** Ignore pathological frame gaps (tab was backgrounded, debugger, …). */
const MAX_FRAME_DT_S = 0.1;

export class GameLoop {
  private lastNow: number | undefined;
  private sendAccumulatorMs = 0;
  private yawSeeded = false;

  constructor(
    private world: World,
    private input: InputController,
    private net: NetClient,
  ) {}

  /** Advance one animation frame. `now` is performance.now() ms. */
  update(now: number): void {
    const dt = Math.min((now - (this.lastNow ?? now)) / 1000, MAX_FRAME_DT_S);
    this.lastNow = now;

    // The first authoritative pose carries the persisted facing; adopt it
    // before any idle intent overwrites it with the default. Re-seeds after
    // every (re)join and portal hop (self is rebuilt each time).
    if (!this.world.self) {
      this.yawSeeded = false;
    } else if (!this.yawSeeded) {
      this.input.seedYaw(this.world.self.serverPose.yaw);
      this.yawSeeded = true;
    }

    const intent = this.input.moveIntent();
    const message: InputMessage = {
      moveX: intent.moveX,
      moveZ: intent.moveZ,
      yaw: intent.yaw,
      sprint: intent.sprint,
      walk: intent.walk,
      climb: intent.climb,
      boost: intent.boost,
    };

    // Predict locally at frame rate for a smooth own avatar — the same
    // integrator the server runs, on foot or at any set of controls.
    const self = this.world.self;
    if (self && dt > 0 && this.world.roomId) {
      const ship = this.world.selfShip;
      // The prediction plan folds in player-raised obstacles (structure
      // shells outside, furniture indoors) exactly as the server does, so
      // walking into a cabin wall is predicted, not corrected.
      const plan = this.world.planForPrediction() ?? planFor(this.world.roomId);
      const skiff = this.world.selfSkiff;
      if (ship) {
        // Everything the tuning derives from is replicated — band words,
        // the routing word, the boost meter — so a boosted, burner-fed
        // climb predicts exactly what the server integrates.
        const entry = this.world.ships.get(ship.id);
        const adrift = entry?.adrift ?? false;
        const tuning = shipFlightTuning(
          entry?.wearBand ?? "true",
          entry?.chargeBand ?? "full",
          entry?.focus ?? "even",
          !adrift && boostActive(message, entry?.boost ?? 0),
        );
        const env = shipEnvFor(planFor(this.world.roomId));
        ship.pose = stepShip(ship.pose, message, dt, env, tuning, adrift);
        self.pose = { x: ship.pose.x, z: ship.pose.z, yaw: ship.pose.yaw };
        this.input.seedYaw(ship.pose.yaw);
      } else if (skiff) {
        // The same band-derived tuning the server integrates with (the
        // words are replicated; the integers never are), so a worn or dry
        // hull predicts correctly instead of rubber-banding.
        const entry = this.world.skiffs.get(skiff.id);
        const tuning = skiffPerformance(entry?.wearBand ?? "true", entry?.chargeBand ?? "full");
        skiff.pose = stepSkiff(skiff.pose, message, dt, plan, tuning);
        self.pose = { x: skiff.pose.x, z: skiff.pose.z, yaw: skiff.pose.yaw };
        // Keep idle facing glued to the nose so dismounting doesn't whip
        // the avatar back to its stale pre-mount walking direction.
        this.input.seedYaw(skiff.pose.yaw);
      } else if (!self.mounted) {
        // Carriage goes in so the prediction respects the same gait
        // ceiling the server applies. Leave it out and a slowed body
        // predicts at full speed and is snapped back several times a
        // second — the failure mode a server-only modifier guarantees.
        self.pose = stepPlayer(self.pose, message, dt, plan, self.carriage);
      }
      // Mounted with no predicted ride = the passenger seat: the body
      // glues to the interpolated hull (world.sampleRemotes), not to
      // input — a passenger's stick moves nothing.
    }

    // …and tell the server what we're trying to do, at tick cadence.
    this.sendAccumulatorMs += dt * 1000;
    if (this.sendAccumulatorMs >= PATCH_INTERVAL_MS) {
      this.sendAccumulatorMs %= PATCH_INTERVAL_MS;
      if (this.world.status === "connected") {
        this.net.sendInput(message);
      }
    }

    this.world.sampleRemotes(now);
  }
}
